import { FormEvent, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faRotate, faFloppyDisk } from "@fortawesome/free-solid-svg-icons";
import { getUser, postUser } from "../../helper/http";
import styles from "./PayductorPage.module.css";

type Section = "methods" | "credentials" | "checkout" | "rates" | "payments";
type Method = {
  code: string; label: string; projectName: string; enabled: boolean;
  checkoutTitle: string; checkoutDescription: string;
  providerMethod: string | null; apiConfigured: boolean; webhookConfigured: boolean; updatedAt: string;
};
type Rate = {
  quote: string; quote_per_usd: string; source: string;
  source_date: string; updated_at: string;
};
type Payment = {
  id: string; method_code: string; wallet_address: string; buyer_email: string;
  amount_usd: string; payment_link_id: string | null; status: string;
  provider_status: string | null; fiat_amount: string | null; fiat_currency: string | null;
  smk_per_usd: string | null; settled_amount_usd: string | null; smk_micro: string | null;
  error_code: string | null; created_at: string; updated_at: string;
};

function errorText(error: any) {
  const message = error?.response?.data?.message;
  return typeof message === "string" ? message : "Request failed. Please try again.";
}

function localDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
}

const nav: { section: Section; label: string }[] = [
  { section: "methods", label: "Methods" },
  { section: "credentials", label: "API keys" },
  { section: "checkout", label: "Checkout settings" },
  { section: "rates", label: "Exchange rates" },
  { section: "payments", label: "Payments" },
];

export default function PayductorPage({ section }: { section: Section }) {
  const [methods, setMethods] = useState<Method[]>([]);
  const [rates, setRates] = useState<Rate[]>([]);
  const [stale, setStale] = useState(false);
  const [smkRate, setSmkRate] = useState("");
  const [savedSmkRate, setSavedSmkRate] = useState<string | null>(null);
  const [minAmount, setMinAmount] = useState("10.00");
  const [maxAmount, setMaxAmount] = useState("1000.00");
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [drafts, setDrafts] = useState<Record<string, { enabled: boolean; providerMethod: string; label: string; checkoutTitle: string; checkoutDescription: string; apiKey: string; apiSecret: string; webhookSecret: string }>>({});

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      if (section === "methods" || section === "credentials" || section === "checkout") {
        const [data, settings] = await Promise.all([
          getUser("api/payductor/admin/methods"),
          ...(section === "checkout" ? [getUser("api/payductor/admin/settings")] : []),
        ]);
        const next = data.methods as Method[];
        setMethods(next);
        setDrafts(Object.fromEntries(next.map((method) => [method.code, {
          enabled: method.enabled, providerMethod: method.providerMethod || "", apiKey: "", apiSecret: "", webhookSecret: "",
          label: method.label, checkoutTitle: method.checkoutTitle, checkoutDescription: method.checkoutDescription,
        }])));
        if (settings) {
          setMinAmount(settings.minAmount);
          setMaxAmount(settings.maxAmount);
        }
      } else if (section === "rates") {
        const [data, settings] = await Promise.all([
          getUser("api/payductor/admin/rates"), getUser("api/payductor/admin/settings"),
        ]);
        setRates(data.rates || []);
        setStale(Boolean(data.stale));
        setSavedSmkRate(settings.smkPerUsd);
        setSmkRate(settings.smkPerUsd || "");
      } else {
        const data = await getUser("api/payductor/admin/payments");
        setPayments(data.payments || []);
      }
    } catch (e) { setError(errorText(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, [section]);

  const updateDraft = (code: string, patch: Partial<(typeof drafts)[string]>) => {
    setDrafts((current) => ({ ...current, [code]: { ...current[code], ...patch } }));
    setNotice("");
  };

  const saveMethod = async (event: FormEvent, code: string) => {
    event.preventDefault();
    const draft = drafts[code];
    if (!draft || busy) return;
    setBusy(code); setError(""); setNotice("");
    try {
      const data = await postUser(`api/payductor/admin/methods/${code}`, {
        ...(section === "methods" ? { enabled: draft.enabled, providerMethod: draft.providerMethod || null } : {}),
        ...(section === "checkout" ? {
          label: draft.label.trim(), checkoutTitle: draft.checkoutTitle.trim(),
          checkoutDescription: draft.checkoutDescription.trim(),
        } : {}),
        ...(section === "credentials" && draft.apiKey.trim() ? { apiKey: draft.apiKey.trim() } : {}),
        ...(section === "credentials" && draft.apiSecret.trim() ? { apiSecret: draft.apiSecret.trim() } : {}),
        ...(section === "credentials" && draft.webhookSecret.trim() ? { webhookSecret: draft.webhookSecret.trim() } : {}),
      });
      setMethods(data.methods || []);
      updateDraft(code, { apiKey: "", apiSecret: "", webhookSecret: "" });
      setNotice(`${code} saved.`);
    } catch (e) { setError(errorText(e)); }
    finally { setBusy(""); }
  };

  const saveLimits = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy("limits"); setError(""); setNotice("");
    try {
      const data = await postUser("api/payductor/admin/settings", { minAmount, maxAmount });
      setMinAmount(data.minAmount);
      setMaxAmount(data.maxAmount);
      setNotice("Checkout limits saved. Existing orders are unchanged.");
    } catch (e) { setError(errorText(e)); }
    finally { setBusy(""); }
  };

  const saveRate = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy("smkRate"); setError(""); setNotice("");
    try {
      const data = await postUser("api/payductor/admin/settings", { smkPerUsd: smkRate.trim() });
      setSavedSmkRate(data.smkPerUsd);
      setSmkRate(data.smkPerUsd);
      setNotice("SMK rate saved. Existing orders retain their original rate.");
    } catch (e) { setError(errorText(e)); }
    finally { setBusy(""); }
  };

  const refreshRates = async () => {
    setBusy("rates"); setError(""); setNotice("");
    try {
      const data = await postUser("api/payductor/admin/rates/refresh");
      setRates(data.rates || []);
      setStale(Boolean(data.stale));
      setNotice("Rates updated.");
    } catch (e) { setError(errorText(e)); }
    finally { setBusy(""); }
  };

  const reconcile = async (id: string) => {
    setBusy(id); setError(""); setNotice("");
    try {
      const data = await postUser(`api/payductor/admin/payments/${id}/reconcile`);
      setNotice(`Provider status: ${data.status || "checked"}.`);
      const current = await getUser("api/payductor/admin/payments");
      setPayments(current.payments || []);
    } catch (e) { setError(errorText(e)); }
    finally { setBusy(""); }
  };

  const webhookRoot = `${window.location.origin}/api/payductor/webhook/`;

  return <main className={styles.page}>
    <header className={styles.header}>
      <div><h1>Payductor</h1><p>Payment methods, reference rates and SMK settlements</p></div>
      <button type="button" className={styles.secondary} onClick={() => void load()} disabled={loading || Boolean(busy)} title="Reload"><FontAwesomeIcon icon={faRotate} /> Reload</button>
    </header>
    <nav className={styles.tabs} aria-label="Payductor sections">
      {nav.map((item) => <NavLink key={item.section} to={`/payductor/${item.section}`} className={({ isActive }) => `${styles.tab} ${isActive ? styles.active : ""}`}>{item.label}</NavLink>)}
    </nav>
    {error && <div role="alert" className={styles.error}>{error}</div>}
    {notice && <div role="status" className={styles.notice}>{notice}</div>}
    {loading ? <p className={styles.empty}>Loading...</p> : section === "methods" || section === "credentials" || section === "checkout" ? <section>
      <div className={styles.sectionHead}><h2>{section === "methods" ? "Methods" : section === "credentials" ? "API keys and webhooks" : "Checkout settings"}</h2><p>{section === "methods" ? "Only enabled methods with configured credentials are available in SMOKE DEV." : section === "credentials" ? "Secrets are write-only and stored encrypted on the backend." : "Customer-facing labels and provider checkout text apply to new payment links."}</p></div>
      {section === "checkout" && <form className={styles.method} onSubmit={(event) => void saveLimits(event)}>
        <div className={styles.methodHead}><div><h3>Order limits</h3><span>Shared by all four payment methods, in USD</span></div></div>
        <div className={styles.fields}>
          <label>Minimum amount<input type="number" min="3" max="1000" step="0.01" required value={minAmount} onChange={(event) => setMinAmount(event.target.value)} /></label>
          <label>Maximum amount<input type="number" min="3" max="1000" step="0.01" required value={maxAmount} onChange={(event) => setMaxAmount(event.target.value)} /></label>
        </div>
        <div className={styles.methodFoot}><button className={styles.primary} type="submit" disabled={Boolean(busy) || Number(minAmount) > Number(maxAmount)}><FontAwesomeIcon icon={faFloppyDisk} /> Save limits</button></div>
      </form>}
      <div className={styles.methodList}>{methods.map((method) => {
        const draft = drafts[method.code];
        if (!draft) return null;
        const webhook = `${webhookRoot}${method.code}`;
        return <form className={styles.method} key={method.code} onSubmit={(e) => void saveMethod(e, method.code)}>
          <div className={styles.methodHead}><div><h3>{method.label}</h3><span>{method.projectName}</span></div>{section === "methods" && <label className={styles.toggle}><input type="checkbox" checked={draft.enabled} onChange={(e) => updateDraft(method.code, { enabled: e.target.checked })} /> Enabled</label>}</div>
          <p className={styles.status}>{method.apiConfigured ? "API credentials set" : "API credentials missing"} · {method.webhookConfigured ? "Webhook secret set" : "No webhook; provider status checked by polling"}</p>
          {section === "methods" && <div className={styles.fields}><label>Provider payment method
            <select value={draft.providerMethod} onChange={(e) => updateDraft(method.code, { providerMethod: e.target.value })}>
              <option value="">Select a method</option>
              <option value="card">Card (USD)</option>
              <option value="applepay">Apple Pay (USD)</option>
              <option value="googlepay">Google Pay (USD)</option>
              <option value="cash-app-v4">Cash App v4 (USD)</option>
              <option value="OPEN_BANKING">Open Banking (EUR)</option>
              <option value="banking">Bank Transfer (EUR)</option>
              <option value="revolut">Revolut (USD)</option>
              <option value="paypal">PayPal (USD)</option>
            </select>
          </label></div>}
          {section === "credentials" && <div className={styles.fields}>
            <label>API key<input type="password" autoComplete="off" value={draft.apiKey} placeholder={method.apiConfigured ? "Leave blank to keep" : "Required"} onChange={(e) => updateDraft(method.code, { apiKey: e.target.value })} /></label>
            <label>API secret<input type="password" autoComplete="off" value={draft.apiSecret} placeholder={method.apiConfigured ? "Leave blank to keep" : "Required"} onChange={(e) => updateDraft(method.code, { apiSecret: e.target.value })} /></label>
            <label>Webhook secret<input type="password" autoComplete="off" value={draft.webhookSecret} placeholder={method.webhookConfigured ? "Leave blank to keep" : "Optional; status is polled"} onChange={(e) => updateDraft(method.code, { webhookSecret: e.target.value })} /></label>
          </div>}
          {section === "checkout" && <div className={styles.fields}>
            <label>Method label<input value={draft.label} required maxLength={80} onChange={(e) => updateDraft(method.code, { label: e.target.value })} /></label>
            <label>Checkout title<input value={draft.checkoutTitle} required maxLength={120} onChange={(e) => updateDraft(method.code, { checkoutTitle: e.target.value })} /></label>
            <label>Checkout description<input value={draft.checkoutDescription} required maxLength={500} onChange={(e) => updateDraft(method.code, { checkoutDescription: e.target.value })} /></label>
          </div>}
          <div className={styles.methodFoot}>{section === "credentials" && <><code>{webhook}</code><button type="button" className={styles.iconButton} title="Copy webhook URL" aria-label="Copy webhook URL" onClick={() => void navigator.clipboard.writeText(webhook)}><FontAwesomeIcon icon={faCopy} /></button></>}<button type="submit" className={styles.primary} disabled={Boolean(busy) || (section === "credentials" && !draft.apiKey.trim() && !draft.apiSecret.trim() && !draft.webhookSecret.trim())}><FontAwesomeIcon icon={faFloppyDisk} /> {busy === method.code ? "Saving..." : "Save"}</button></div>
        </form>;
      })}</div>
    </section> : section === "rates" ? <section>
      <div className={styles.sectionHead}><div><h2>SMK and currency rates</h2><p>The SMK rate is captured when an order is created. Reference FX is used only to price EUR Open Banking checkouts.</p></div><button className={styles.secondary} onClick={() => void refreshRates()} disabled={Boolean(busy)}><FontAwesomeIcon icon={faRotate} /> Refresh FX</button></div>
      <form className={styles.method} onSubmit={(event) => void saveRate(event)}><div className={styles.methodHead}><div><h3>SMK settlement rate</h3><span>{savedSmkRate ? `Current: 1 USD = ${savedSmkRate} SMK` : "Set a rate before enabling payments"}</span></div></div><div className={styles.fields}><label>SMK for 1 settled USD<input type="number" min="0.001" max="1000" step="0.000001" required value={smkRate} onChange={(event) => setSmkRate(event.target.value)} /></label></div><div className={styles.methodFoot}><button className={styles.primary} disabled={Boolean(busy)} type="submit"><FontAwesomeIcon icon={faFloppyDisk} /> Save rate</button></div></form>
      {stale && <div role="status" className={styles.warning}>Reference rates are stale.</div>}
      <div className={styles.tableWrap}><table><thead><tr><th>Pair</th><th>1 USD buys</th><th>Source date</th><th>Updated</th></tr></thead><tbody>{rates.map((rate) => <tr key={rate.quote}><td>USD / {rate.quote}</td><td>{rate.quote_per_usd}</td><td>{rate.source_date}</td><td>{localDate(rate.updated_at)}</td></tr>)}</tbody></table>{rates.length === 0 && <p className={styles.empty}>No rates available.</p>}</div>
    </section> : <section>
      <div className={styles.sectionHead}><h2>Payments</h2><p>Credit is issued once, after a verified final settlement. Reconcile checks the provider directly.</p></div>
      <div className={styles.tableWrap}><table><thead><tr><th>Created</th><th>Method</th><th>Wallet</th><th>Order</th><th>Settled</th><th>SMK</th><th>Status</th><th>Action</th></tr></thead><tbody>{payments.map((payment) => <tr key={payment.id}><td>{localDate(payment.created_at)}</td><td>{payment.method_code}</td><td><code title={payment.wallet_address}>{payment.wallet_address.slice(0, 7)}…{payment.wallet_address.slice(-5)}</code></td><td>${payment.amount_usd}<small>{payment.fiat_amount && `${payment.fiat_amount} ${payment.fiat_currency}`}</small></td><td>{payment.settled_amount_usd ? `$${payment.settled_amount_usd}` : "-"}</td><td>{payment.smk_micro ? `${(Number(payment.smk_micro) / 1000000).toFixed(2)} SMK` : payment.smk_per_usd ? `@ ${payment.smk_per_usd}` : "-"}</td><td><span className={styles.badge}>{payment.status}</span>{payment.provider_status && <small>{payment.provider_status}</small>}{payment.error_code && <small>{payment.error_code}</small>}</td><td>{payment.payment_link_id && payment.status !== "credited" && <button className={styles.secondary} disabled={Boolean(busy)} onClick={() => void reconcile(payment.id)}>Reconcile</button>}</td></tr>)}</tbody></table>{payments.length === 0 && <p className={styles.empty}>No payments yet.</p>}</div>
    </section>}
  </main>;
}
